import React, { useState, useEffect } from "react";
import { FaTimesCircle } from "react-icons/fa";

import promptService from "../../services/promptService";

const PromptProblemsTab = ({ prompt, language }) => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isPython = language === "python";
  const noProblemsMessage = isPython
    ? "No problems detected in the Python code."
    : "No problems detected in the prompt.";

  useEffect(() => {
    // Don't attempt to lint if there's no prompt content
    if (!prompt) {
      setProblems([]);
      return;
    }
    setLoading(true);

    const fetchLintingProblems = async () => {
      try {
        const response = await promptService.lintPrompt(
          prompt,
          isPython ? "python" : "interactive",
        );

        setProblems(response || []);
        setLoading(false);
      } catch (err) {
        console.error("Error during linting:", err);
        setError(err.response?.data?.message || "Failed to analyze Prompt");
        setProblems([]);
      }
    };

    // Use a debounce to prevent too frequent API calls during typing
    const timeoutId = setTimeout(() => {
      fetchLintingProblems();
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [prompt, isPython]);

  /**
   * Format the problem text to highlight the problematic section for Python errors
   * @param {Object} problem - The problem object to format
   * @returns {JSX.Element} Formatted text with highlighted error
   */
  const formatPythonProblemText = (problem) => {
    if (
      !problem.column_start ||
      !problem.column_end ||
      problem.column_start >= problem.column_end
    ) {
      return (
        <div className="w-full text-sm">
          <div className="text-gray-200">
            {problem.message}: Line {problem.line}
          </div>
          <div className="text-gray-500">
            <div className="text-gray-500 mt-1 pl-2 border-l-2 border-gray-700">
              <span className="font-mono">{problem.problem_text}</span>
            </div>
          </div>
        </div>
      );
    }

    // Make sure we have valid ranges
    const start = Math.max(0, problem.column_start - 1);
    const end = Math.min(problem.problem_text.length, problem.column_end - 1);

    // Extract the different parts of the text
    const before = problem.problem_text.substring(0, start);
    const problematic = problem.problem_text.substring(start, end);
    const after = problem.problem_text.substring(end);

    return (
      <div className="w-full text-sm">
        <div className="text-gray-200">
          {problem.message}: Line {problem.line}
        </div>
        <div className="text-gray-500">
          <div className="text-gray-500 mt-1 pl-2 border-l-2 border-gray-700">
            <span className="font-mono">
              {before}
              <span className="bg-red-900 text-red-200 px-0.5 rounded">
                {problematic}
              </span>
              {after}
            </span>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Format the problem text to highlight the problematic section
   * @param {Object} problem - The problem object to format
   * @returns {JSX.Element} Formatted text with highlighted error
   */
  const formatJinjaProblemText = (problem) => {
    return (
      <div className="w-full text-sm">
        <div className="text-gray-200">Template Error: Line {problem.line}</div>
        <div className="text-gray-500">
          <div className="text-gray-500 mt-1 pl-2 border-l-2 border-gray-700">
            <span className="font-mono">{problem.message}</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4 text-gray-400 flex items-center justify-center h-full text-sm">
        <p>Analyzing prompt ...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-400 text-sm">
        <h3 className="font-semibold mb-2">Error analyzing code</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-[#1E1E1E] text-sm overflow-auto h-full">
      {problems.length === 0 ? (
        <div className="p-4 text-gray-400 flex items-center justify-center h-full">
          <p>{noProblemsMessage}</p>
        </div>
      ) : (
        <div>
          <div className="px-4 py-2 border-b border-gray-700 text-gray-300">
            {problems.length} {problems.length === 1 ? "problem" : "problems"}{" "}
            found
          </div>
          <ul>
            {problems.map((problem, index) => (
              <li
                key={index}
                className="px-4 py-2 border-b border-gray-800 hover:bg-gray-800 cursor-pointer"
              >
                <div className="flex items-start">
                  <FaTimesCircle className="text-red-500 mr-2 mt-1 flex-shrink-0" />
                  <div className="w-full">
                    {isPython
                      ? formatPythonProblemText(problem)
                      : formatJinjaProblemText(problem)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PromptProblemsTab;
